import { Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';
import { readStoredExternalChannelCampaign } from '../utils/external-channel-access.util';

const GTM_CAMPAIGN_EVENTS = new Set([
  'add_to_cart',
  'view_item',
  'view_item_list',
  'begin_checkout',
  'purchase',
  'login',
  'sign_up',
]);

export interface Ga4Item {
  item_id?: string | number;
  item_name?: string;
  affiliation?: string;
  coupon?: string;
  currency?: string;
  discount?: number;
  index?: number;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_variant?: string;
  location_id?: string;
  price?: number;
  quantity?: number;
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

@Injectable({ providedIn: 'root' })
export class GtmService {
  private containerId: string | undefined;
  private initialized = false;

  init(): void {
    if (this.initialized) return;
    if (typeof window === 'undefined') return;

    this.containerId = environment?.gtm?.containerId;
    if (!this.containerId) {
      console.warn('[GTM] containerId ausente em environment');
      return;
    }

    window.dataLayer = window.dataLayer || [];

    this.push('consent', {
      default: {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'granted'
      }
    });

    // Evita injeção duplicada caso o snippet já esteja em index.html
    const existingGtmScript = document.querySelector('script[src*="googletagmanager.com/gtm.js"]');
    if (!existingGtmScript) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${this.containerId}`;
      document.head.appendChild(script);
    }

    // Noscript (apenas se não estiver presente)
    const existingNoScript = document.querySelector('noscript iframe[src*="googletagmanager.com/ns.html"]');
    if (!existingNoScript) {
      const noScript = document.createElement('noscript');
      noScript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${this.containerId}"
        height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.body.appendChild(noScript);
    }

    this.initialized = true;
  }

  push(event: string, params?: Record<string, unknown>): void {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];

    const campaign = GTM_CAMPAIGN_EVENTS.has(event)
      ? readStoredExternalChannelCampaign()
      : '';
    const campaignParams = campaign ? { campaign } : {};

    window.dataLayer.push({
      event,
      ...(params || {}),
      ...campaignParams,
    });
  }

  // API genérica preferencial
  track(eventName: string, params?: Record<string, unknown>): void {
    this.push(eventName, params);
  }

  /**
   * Açúcar opcional. Prefira usar `track('page_view', { ... })`.
   */
  pushPageView(pageLocation: string, pageTitle?: string, route?: string): void {
    this.push('page_view', {
      page_location: pageLocation,
      page_title: pageTitle || document.title,
      route
    });
  }

  /**
   * Açúcar opcional. Prefira `track('set_user', { user_id, user_email })`.
   */
  setUser(userId?: string, email?: string): void {
    if (!userId && !email) return;
    this.push('set_user', {
      user_id: userId,
      user_email: email
    });
  }

  /** Açúcar opcional para e-commerce. */
  viewItemList(listId: string, listName: string, items: Ga4Item[]): void {
    this.push('view_item_list', {
      item_list_id: listId,
      item_list_name: listName,
      items
    });
  }

  /** Açúcar opcional para e-commerce. */
  selectItem(listId: string, listName: string, item: Ga4Item): void {
    this.push('select_item', {
      item_list_id: listId,
      item_list_name: listName,
      items: [item]
    });
  }

  /** Açúcar opcional para e-commerce. */
  viewItem(item: Ga4Item): void {
    this.push('view_item', {
      currency: 'BRL',
      value: item.price ?? 0,
      items: [item]
    });
  }

  /** Açúcar opcional para e-commerce. */
  addToCart(items: Ga4Item[], value?: number): void {
    this.push('add_to_cart', {
      currency: 'BRL',
      value: value ?? this.sumItems(items),
      items
    });
  }

  /** Açúcar opcional para e-commerce. */
  removeFromCart(items: Ga4Item[], value?: number): void {
    this.push('remove_from_cart', {
      currency: 'BRL',
      value: value ?? this.sumItems(items),
      items
    });
  }

  /** Açúcar opcional para e-commerce. */
  viewCart(items: Ga4Item[], value?: number): void {
    this.push('view_cart', {
      currency: 'BRL',
      value: value ?? this.sumItems(items),
      items
    });
  }

  /** Açúcar opcional para e-commerce. */
  beginCheckout(items: Ga4Item[], value?: number): void {
    this.push('begin_checkout', {
      currency: 'BRL',
      value: value ?? this.sumItems(items),
      items
    });
  }

  /** Açúcar opcional para e-commerce. */
  purchase(transactionId: string, items: Ga4Item[], value: number): void {
    this.push('purchase', {
      currency: 'BRL',
      transaction_id: transactionId,
      value,
      items
    });
  }

  /** Açúcar opcional. */
  login(method: string, userId?: string): void {
    this.push('login', {
      method,
      user_id: userId
    });
  }

  /** Açúcar opcional. */
  signUp(method: string, userId?: string): void {
    this.push('sign_up', {
      method,
      user_id: userId
    });
  }

  private sumItems(items: Ga4Item[]): number {
    return (items || []).reduce((acc, it) => acc + (it.price ?? 0) * (it.quantity ?? 1), 0);
  }
}


